import { useEffect } from 'react'

import * as d3 from 'd3'

import { BoxType, CornerPoint, TailgateBox } from '../types/types'
import { CORNER_RADIUS, getClosestPointWithinImageBounds } from '../utils/utils'

export const useDrawBox = ({
  inBox,
  outBox,
  onDeviceUpdated,
  frame,
  svgRef,
}) => {
  /**
   * This function is called when dragging a TailgateBox corner point
   */
  const handleCornerDrag = d3
    .drag()
    .on('drag', (event: DragEvent, d: CornerPoint) => {
      const { x, y } = getClosestPointWithinImageBounds(
        event.x,
        event.y,
        frame?.width,
        frame?.height
      )
      d.x = x
      d.y = y
      onDeviceUpdated(d)
    })
    .on('end', (event, d: CornerPoint) => {
      const { x, y } = getClosestPointWithinImageBounds(
        event.x,
        event.y,
        frame?.width,
        frame?.height
      )
      d.x = x
      d.y = y
      onDeviceUpdated(d)
    })

  /**
   * This function is used to correct the line points of the TailgateBox.
   * Returns a path string.
   */
  const connectLines = (box: TailgateBox): string => {
    if (box.corners.length < 4) {
      return null
    }
    const lineCoords: [number, number][] = [
      [box.corners[0]?.x, box.corners[0]?.y],
      [box.corners[1]?.x, box.corners[1]?.y],
      [box.corners[2]?.x, box.corners[2]?.y],
      [box.corners[3]?.x, box.corners[3]?.y],
      [box.corners[0]?.x, box.corners[0]?.y],
    ]

    const line = d3.line()
    return line(lineCoords)
  }

  /**
   * This function is used to draw the TailgateBox corner points.
   * It is called to draw any new/entering data.
   * Returns the d3 SVGGElement
   */
  const cornerEnter = (
    enter: d3.Selection<d3.EnterElement, unknown, d3.BaseType, unknown>
  ) => {
    const g = enter
      .append('g')
      .attr('class', 'box')
      .attr('id', (d: CornerPoint) =>
        d.type === BoxType.IN_BOX
          ? `inBox-corner-${d.id}`
          : `outBox-corner-${d.id}`
      )
      .attr('transform', (d: CornerPoint) => `translate(${d.x}, ${d.y})`)
      .call(handleCornerDrag)

    g.append('circle')
      .attr('class', (d: CornerPoint) =>
        d.type === BoxType.IN_BOX ? 'inBoxCorner' : 'outBoxCorner'
      )
      .attr('id', (d: CornerPoint) => `inBoxCorner-${d.id}`)
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', CORNER_RADIUS)

    return g
  }

  /**
   * This function is used to draw the TailgateBox corner points.
   * It is called to UPDATE the data.
   * Returns the d3 SVGGElement
   */
  const cornerUpdate = (
    update: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>
  ) => {
    update.attr('transform', (d: CornerPoint) => `translate(${d.x}, ${d.y})`)
    update.attr('id', (d: CornerPoint) =>
      d.type === BoxType.IN_BOX
        ? `inBox-corner-${d.id}`
        : `outBox-corner-${d.id}`
    )
    return update
  }

  /**
   * This function is used to draw the TailgateBox lines/path points.
   * It is called to draw any new/entering data.
   * Returns the d3 SVGGElement
   */
  const linesEnter = (
    enter: d3.Selection<d3.EnterElement, unknown, d3.BaseType, unknown>,
    type: BoxType,
    isValid: boolean
  ) => {
    const g = enter.append('g').attr('class', 'lines')
    g.append('path')
      .attr('class', type === BoxType.IN_BOX ? 'inBoxPath' : 'outBoxPath')
      .classed('inValidBox', !isValid)
      .attr('d', connectLines(type === BoxType.IN_BOX ? inBox : outBox))

    return g
  }

  /**
   * This function is used to draw the TailgateBox lines/path points.
   * It is called to UPDATE the data.
   * Returns the d3 SVGGElement
   */
  const linesUpdate = (
    update: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>,
    type: BoxType,
    isValid: boolean
  ) => {
    update
      .select('path')
      .classed('inValidBox', !isValid)
      .attr('d', connectLines(type === BoxType.IN_BOX ? inBox : outBox))
    return update
  }

  /**
   * This function handles updating all the svg elements.
   * Selects the different SVGGElements by their className.
   * Joins the SVGGElements with the inputed data array.
   * Provides functions to handle drawing, updating and existing of the SVGGElement.
   */
  const renderDevices = () => {
    if (!svgRef?.current) return

    const svg = d3.select(svgRef.current)

    // Clear SVG when data is unavailable
    if (!inBox || !outBox) {
      svg.select('.outBox-corner-group').selectAll('g').remove()
      svg.select('.outBox-line-group').selectAll('g').remove()
      svg.select('.inBox-corner-group').selectAll('g').remove()
      svg.select('.inBox-line-group').selectAll('g').remove()
      return
    }

    svg
      .select('.outBox-corner-group')
      .selectAll('g')
      .data(outBox.corners)
      .join(cornerEnter, cornerUpdate, (exit) => exit.remove())

    svg
      .select('.outBox-line-group')
      .selectAll('g')
      .data(outBox.corners)
      .join(
        (u) => linesEnter(u, BoxType.OUT_BOX, outBox.isValid),
        (u) => linesUpdate(u, BoxType.OUT_BOX, outBox.isValid),
        (exit) => exit.remove()
      )

    svg
      .select('.inBox-corner-group')
      .selectAll('g')
      .data(inBox.corners)
      .join(cornerEnter, cornerUpdate, (exit) => exit.remove())

    svg
      .select('.inBox-line-group')
      .selectAll('g')
      .data(inBox.corners)
      .join(
        (u) => linesEnter(u, BoxType.IN_BOX, inBox.isValid),
        (u) => linesUpdate(u, BoxType.IN_BOX, inBox.isValid),
        (exit) => exit.remove()
      )
  }

  /**
   * This gets called anytime the inBox/outBox TailgateBox data gets updated
   * Calls renderDevices() to draw/update the data
   */
  useEffect(renderDevices, [inBox, outBox])
}
